/**
 * Boot Model Formulier - Nieuw/Bewerken
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { bootModellenService, BootModelCreate } from '../../services/bootModellen.service';
import ErrorDialog from '../../components/ErrorDialog';
import Toast from '../../components/Toast';
import './BootModellen.css';

interface BootModelFormProps {
  bootModelId?: number;
}

const BootModelForm = ({ bootModelId }: BootModelFormProps) => {
  const navigate = useNavigate();
  const isEditMode = !!bootModelId;

  const [formData, setFormData] = useState<BootModelCreate>({
    fabrikant: '',
    model: '',
    type_boot: '',
    materiaal: '',
    lengte: undefined,
    breedte: undefined,
    diepgang: undefined,
    gewicht: undefined,
    bouwjaar_vanaf: undefined,
    bouwjaar_tot: undefined,
    omschrijving: '',
    actief: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadBootModel = useCallback(async () => {
    if (!bootModelId) return;

    try {
      setLoading(true);
      const model = await bootModellenService.getById(bootModelId);
      setFormData({
        fabrikant: model.fabrikant,
        model: model.model,
        type_boot: model.type_boot || '',
        materiaal: model.materiaal || '',
        lengte: model.lengte,
        breedte: model.breedte,
        diepgang: model.diepgang,
        gewicht: model.gewicht,
        bouwjaar_vanaf: model.bouwjaar_vanaf,
        bouwjaar_tot: model.bouwjaar_tot,
        omschrijving: model.omschrijving || '',
        actief: model.actief,
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [bootModelId]);

  useEffect(() => {
    if (isEditMode) {
      loadBootModel();
    }
  }, [isEditMode, loadBootModel]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'number'
          ? value === ''
            ? undefined
            : parseFloat(value)
          : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (isEditMode && bootModelId) {
        await bootModellenService.update(bootModelId, formData);
        setSuccess('Boot model succesvol bijgewerkt!');
      } else {
        await bootModellenService.create(formData);
        setSuccess('Boot model succesvol aangemaakt!');
      }

      setTimeout(() => {
        navigate('/boot-modellen');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="boot-model-form">
      <div className="page-header">
        <h1>{isEditMode ? 'Boot Model Bewerken' : 'Nieuw Boot Model'}</h1>
      </div>

      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      <form onSubmit={handleSubmit}>
        {/* Basis Informatie */}
        <section className="form-section">
          <h2>Basis Informatie</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="fabrikant">
                Fabrikant <span className="required">*</span>
              </label>
              <input
                type="text"
                id="fabrikant"
                name="fabrikant"
                value={formData.fabrikant}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="model">
                Model <span className="required">*</span>
              </label>
              <input
                type="text"
                id="model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="type_boot">Type Boot</label>
              <select
                id="type_boot"
                name="type_boot"
                value={formData.type_boot}
                onChange={handleChange}
              >
                <option value="">-- Selecteer type --</option>
                <option value="Motorjacht">Motorjacht</option>
                <option value="Zeiljacht">Zeiljacht</option>
                <option value="Sloep">Sloep</option>
                <option value="Kajuitsloep">Kajuitsloep</option>
                <option value="Speedboot">Speedboot</option>
                <option value="Catamaran">Catamaran</option>
                <option value="Kotter">Kotter</option>
                <option value="Trawler">Trawler</option>
                <option value="RIB">RIB</option>
                <option value="Rubberboot">Rubberboot</option>
                <option value="Platbodem">Platbodem</option>
                <option value="Open zeilboot">Open zeilboot</option>
              </select>
            </div>
          </div>
        </section>

        {/* Specificaties */}
        <section className="form-section">
          <h2>Specificaties</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="lengte">Lengte (m)</label>
              <input
                type="number"
                id="lengte"
                name="lengte"
                value={formData.lengte ?? ''}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="breedte">Breedte (m)</label>
              <input
                type="number"
                id="breedte"
                name="breedte"
                value={formData.breedte ?? ''}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="diepgang">Diepgang (m)</label>
              <input
                type="number"
                id="diepgang"
                name="diepgang"
                value={formData.diepgang ?? ''}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="gewicht">Gewicht (kg)</label>
              <input
                type="number"
                id="gewicht"
                name="gewicht"
                value={formData.gewicht ?? ''}
                onChange={handleChange}
                step="1"
                min="0"
                placeholder="Geheel getal in kg"
              />
            </div>

            <div className="form-group">
              <label htmlFor="materiaal">Materiaal</label>
              <select
                id="materiaal"
                name="materiaal"
                value={formData.materiaal}
                onChange={handleChange}
              >
                <option value="">-- Selecteer materiaal --</option>
                <option value="Staal">Staal</option>
                <option value="Aluminium">Aluminium</option>
                <option value="Polyester">Polyester</option>
                <option value="GVK">GVK</option>
                <option value="Hout">Hout</option>
                <option value="Composiet">Composiet</option>
                <option value="Carbon">Carbon</option>
                <option value="PVC">PVC</option>
                <option value="Hypalon">Hypalon</option>
              </select>
            </div>
          </div>
        </section>

        {/* Productie Periode */}
        <section className="form-section">
          <h2>Productie Periode</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="bouwjaar_vanaf">Bouwjaar Vanaf</label>
              <input
                type="number"
                id="bouwjaar_vanaf"
                name="bouwjaar_vanaf"
                value={formData.bouwjaar_vanaf ?? ''}
                onChange={handleChange}
                min="1900"
                max="2100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bouwjaar_tot">Bouwjaar Tot</label>
              <input
                type="number"
                id="bouwjaar_tot"
                name="bouwjaar_tot"
                value={formData.bouwjaar_tot ?? ''}
                onChange={handleChange}
                min="1900"
                max="2100"
              />
            </div>
          </div>
        </section>

        {/* Omschrijving */}
        <section className="form-section">
          <h2>Omschrijving</h2>
          <div className="form-group">
            <textarea
              id="omschrijving"
              name="omschrijving"
              value={formData.omschrijving}
              onChange={handleChange}
              rows={4}
              placeholder="Aanvullende informatie over dit boot model..."
            />
          </div>
        </section>

        <div className="form-actions">
          <button
            type="button"
            className="btn-lg btn-secondary"
            onClick={() => navigate('/boot-modellen')}
          >
            Annuleren
          </button>
          <button type="submit" className="btn-lg btn-success" disabled={loading}>
            {loading ? 'Bezig...' : isEditMode ? 'Bijwerken' : 'Aanmaken'}
          </button>
        </div>
      </form>

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default BootModelForm;